import { AxiosResponse } from 'axios';
import { getCookie } from 'commercial-web-client-utilities/dist/cookie';
import { NonsecureService } from 'commercial-web-client-utilities/dist/composables/nonsecure-service';
import type { LedsService } from 'commercial-web-client-utilities/dist/composables/leds-service';
import {
    ContactChannel,
    OneTimePasswordRequest
} from '@/types/mfa';
import { MultiFactorStatusCode } from '@/enums/multi-factor-auth';
import { DeviceInfo } from '@/types/device-info';


export interface MemberMfaServiceOptions {
    ledsService: LedsService;
    nonsecureService: NonsecureService;
}

/**
 * Define service APIs consumable by the client
 * @property signal {AbortSignal}
 */
export interface MemberMfaService {
    getMemberContactChannels(options: MemberContactChannelsRequestOptions):
        Promise<AxiosResponse<ContactChannelsrespons>>;
    requestMemberOneTimePassword(options: MemberOneTimePasswordRequestOptions):
        Promise<AxiosResponse<OneTimePasswordResponse>>;
    validateOneTimePassword(options: MemberOneTimePasswordValidationOptions):
        Promise<AxiosResponse<OneTimePasswordValidationResponse>>;
}

interface MemberContactChannelsRequestOptions {
    signal?: AbortSignal;
    mfaToken: string;
}

interface MemberOneTimePasswordRequest {
    channelId: string;
    deviceInfo: DeviceInfo;
}
interface MemberOneTimePasswordRequestOptions {
    signal?: AbortSignal;
    mfaToken: string;
    payload: MemberOneTimePasswordRequest;
}

interface MemberOneTimePasswordValidationOptions {
    signal?: AbortSignal;
    payload: OneTimePasswordRequest;
}

export interface OneTimePasswordResponse {
    mfaToken: string;
}

interface OneTimePasswordValidationResponse {
    authToken?: string;
    updatePasswordToken?: string;
}

export interface ContactChannelsrespons {
    contactChannels: ContactChannel[];
    mfaToken: string;
}

export function createMemberMfaService(memberMfaServiceOptions:
    MemberMfaServiceOptions): MemberMfaService {
    const { nonsecureService, ledsService } = memberMfaServiceOptions;
    const nonSecureServiceClient = nonsecureService.client;
    const ledsClient = ledsService.client;
    const region = getCookie('selectedRegion');

    async function getMemberContactChannels(options: MemberContactChannelsRequestOptions):
    Promise<AxiosResponse<ContactChannelsrespons>> {
        const signal = options.signal;
        const token = options.mfaToken;
        const headers = await ledsService.createAuthorizationHeaders();
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        if (region) headers['X-Region'] = region;
        const serviceUrl = `${ledsClient.defaults.baseURL}member/authenticate/multifactor/contactChannels`;
        const response = await nonSecureServiceClient.get(serviceUrl, { headers, signal });
        response.data = response?.data ? response.data : {};
        return response;
    }

    async function requestMemberOneTimePassword(options: MemberOneTimePasswordRequestOptions):
    Promise<AxiosResponse<OneTimePasswordResponse>> {
        const signal = options.signal;
        const token = options.mfaToken;
        const payload = options.payload;
        const headers = await ledsService.createAuthorizationHeaders();
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        if (region) headers['X-Region'] = region;
        const serviceUrl = `${ledsClient.defaults.baseURL}member/authenticate/multifactor/challenge`;
        const response = await nonSecureServiceClient.post(serviceUrl, payload, { headers, signal });
        response.data = response?.data ? response.data : {};
        return response;
    }

    async function validateOneTimePassword(options: MemberOneTimePasswordValidationOptions):
    Promise<AxiosResponse<OneTimePasswordValidationResponse>> {
        const signal = options.signal;
        const payload = options.payload;
        const headers = await ledsService.createAuthorizationHeaders();
        const serviceUrl = `${ledsClient.defaults.baseURL}member/authenticate/oneTimePassword`;
        if (region) headers['X-Region'] = region;
        const response = await nonSecureServiceClient.post(serviceUrl, { ...payload }, {
            headers,
            signal,
            validateStatus: status => [MultiFactorStatusCode.MULTIFACTOR_SUCCESS,
                MultiFactorStatusCode.MULTIFACTOR_REDIRECT_PASSWORD_INTERRUPT].includes(status)
        });
        response.data = response?.data ? response.data : {};
        return response;
    }

    return {
        getMemberContactChannels,
        requestMemberOneTimePassword,
        validateOneTimePassword
    };

}
